import React from 'react';
import { Animated, KeyboardAvoidingView, Modal, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text, TextInput } from './AppText';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Spacing } from '../theme';
import { AnimatedProgress, PressableScale, useSheetMotion } from './Motion';
import { CATEGORIES, CategoryId, normalizeCategory } from '../types';

export const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export function formatBRL(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function moveMonth(month: number, year: number, delta: number) {
  const next = new Date(year, month - 1 + delta, 1);
  return { month: next.getMonth() + 1, year: next.getFullYear() };
}

export function categoryIconName(category: CategoryId): React.ComponentProps<typeof Ionicons>['name'] {
  const icons: Record<CategoryId, React.ComponentProps<typeof Ionicons>['name']> = {
    alimentacao: 'restaurant-outline', moradia: 'home-outline', transporte: 'car-outline',
    saude: 'medkit-outline', lazer: 'game-controller-outline', educacao: 'book-outline',
    vestuario: 'shirt-outline', assinaturas: 'phone-portrait-outline', investimentos: 'trending-up-outline',
    pessoal: 'person-outline', presentes: 'gift-outline', outros: 'cube-outline',
  };
  return icons[category] ?? 'cube-outline';
}

type IoniconName = React.ComponentProps<typeof Ionicons>['name'];

export interface CommitmentVisual {
  icon: IoniconName;
  color: string;
  background: string;
}

const semanticCommitmentVisuals: Array<{ words: string[]; icon: IoniconName; color: string }> = [
  { words: ['energia', 'eletricidade', 'luz'], icon: 'flash-outline', color: '#F59E0B' },
  { words: ['agua', 'água'], icon: 'water-outline', color: '#0EA5E9' },
  { words: ['internet', 'wifi', 'wi-fi'], icon: 'wifi-outline', color: '#6366F1' },
  { words: ['serasa', 'seguro'], icon: 'shield-checkmark-outline', color: '#8B5CF6' },
  { words: ['cartao', 'cartão', 'nubank', 'mastercard', 'visa', 'elo', 'amex', 'hipercard', 'mercado pago', 'mercadopago'], icon: 'card-outline', color: '#0B7A78' },
];

function normalizeVisualText(value?: string | null) {
  return (value ?? '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Uses a recognizable service icon first, then falls back to the category icon. */
export function commitmentVisual(name?: string | null, category?: string | null, type?: string | null): CommitmentVisual {
  if (type === 'invoice') return { icon: 'card-outline', color: Colors.primary, background: Colors.primaryLt };

  const normalizedName = normalizeVisualText(name);
  const semantic = semanticCommitmentVisuals.find(item => item.words.some(word => normalizedName.includes(normalizeVisualText(word))));
  if (semantic) return { icon: semantic.icon, color: semantic.color, background: `${semantic.color}22` };

  const definition = CATEGORIES.find(item => item.id === normalizeCategory(category)) ?? CATEGORIES[CATEGORIES.length - 1];
  return { icon: categoryIconName(definition.id), color: definition.color, background: `${definition.color}22` };
}

export function DarkHeader({ title, month, year, onPrev, onNext, action }: {
  title: string;
  month: number;
  year: number;
  onPrev: () => void;
  onNext: () => void;
  action?: React.ReactNode;
}) {
  return (
    <View style={styles.darkHeader}>
      <View style={styles.headerTop}>
        <Text style={styles.headerTitle}>{title}</Text>
        {action}
      </View>
      <View style={styles.monthRow}>
        <PressableScale onPress={onPrev} accessibilityLabel="Mês anterior" hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} containerStyle={styles.monthButton} contentStyle={styles.monthButtonContent}>
          <Ionicons name="chevron-back" size={15} color={Colors.text2} />
        </PressableScale>
        <Text style={styles.monthText}>{MONTH_NAMES[month - 1]} {year}</Text>
        <PressableScale onPress={onNext} accessibilityLabel="Próximo mês" hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} containerStyle={styles.monthButton} contentStyle={styles.monthButtonContent}>
          <Ionicons name="chevron-forward" size={15} color={Colors.text2} />
        </PressableScale>
      </View>
    </View>
  );
}

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <Text style={styles.sectionLabel}>{children}</Text>;
}

export function SurfaceCard({ children, style }: { children: React.ReactNode; style?: any }) {
  return <View style={[styles.surfaceCard, style]}>{children}</View>;
}

export function CategoryChip({ category, selected, onPress, compact = false }: { category: CategoryId; selected?: boolean; onPress?: () => void; compact?: boolean }) {
  const item = CATEGORIES.find(value => value.id === category) ?? CATEGORIES[CATEGORIES.length - 1];
  return (
    <PressableScale onPress={onPress} containerStyle={[styles.chipPress, compact && styles.chipCompact]} contentStyle={[styles.chip, selected && styles.chipSelected]} accessibilityLabel={`Categoria ${item.name}`} accessibilityState={{ selected }}>
      <Ionicons name={categoryIconName(item.id)} size={compact ? 16 : 15} color={selected ? Colors.primary : Colors.text2} />
      {!compact && <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{item.name}</Text>}
    </PressableScale>
  );
}

export function CategoryPicker({ value, onChange }: { value: CategoryId; onChange: (value: CategoryId) => void }) {
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoryPicker}>
      {CATEGORIES.map(category => <CategoryChip key={category.id} category={category.id} selected={value === category.id} onPress={() => onChange(category.id)} />)}
    </ScrollView>
  );
}

export function BudgetBar({ category, spent, budget }: { category: CategoryId; spent: number; budget?: number }) {
  const definition = CATEGORIES.find(value => value.id === category) ?? CATEGORIES[CATEGORIES.length - 1];
  const ratio = budget && budget > 0 ? Math.min(spent / budget, 1) : 0;
  const color = budget && spent > budget ? Colors.expense : budget && spent > budget * 0.8 ? Colors.warning : definition.color;
  return (
    <View style={styles.budgetRow}>
      <View style={styles.budgetMeta}>
        <View style={styles.budgetName}><Ionicons name={categoryIconName(definition.id)} size={14} color={definition.color} /><Text style={styles.budgetLabel}>{definition.name}</Text></View>
        <Text style={styles.budgetAmount}>{budget ? `${formatBRL(spent)} / ${formatBRL(budget)}` : formatBRL(spent)}</Text>
      </View>
      {budget ? <AnimatedProgress value={Math.max(ratio * 100, spent > 0 ? 3 : 0)} color={color} trackStyle={styles.track} fillStyle={styles.fill} /> : null}
    </View>
  );
}

export function FormModal({ visible, title, onClose, children, onSave, saving = false }: { visible: boolean; title: string; onClose: () => void; children: React.ReactNode; onSave: () => void; saving?: boolean }) {
  const motion = useSheetMotion(visible);
  return (
    <Modal visible={visible} animationType="none" transparent onRequestClose={onClose}>
      <Animated.View style={[styles.modalOverlay, motion.backdrop]} accessibilityRole={Platform.OS === 'web' ? ('dialog' as any) : 'none'} accessibilityLabel={title} accessibilityViewIsModal>
        <Animated.View style={[styles.modalSheet, motion.sheet]}>
          <View style={styles.handle} />
          <KeyboardAvoidingView style={styles.modalBody} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
            <View style={styles.modalTop}>
              <PressableScale onPress={onClose} accessibilityLabel="Cancelar" containerStyle={styles.navPress} contentStyle={styles.cancelButton}>
                <Text style={styles.cancelText}>Cancelar</Text>
              </PressableScale>
              <Text style={styles.modalTitle} numberOfLines={1}>{title}</Text>
              <PressableScale onPress={onSave} disabled={saving} accessibilityLabel={saving ? 'Salvando' : 'Salvar'} containerStyle={styles.navPress} contentStyle={styles.saveButton}>
                <Text style={styles.saveText}>{saving ? 'Salvando…' : 'Salvar'}</Text>
              </PressableScale>
            </View>
            <ScrollView style={styles.formScroll} contentContainerStyle={styles.form} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
              {children}
            </ScrollView>
          </KeyboardAvoidingView>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}

export function FormField({ label, value, onChangeText, placeholder, keyboardType = 'default' }: { label: string; value: string; onChangeText: (value: string) => void; placeholder?: string; keyboardType?: 'default' | 'decimal-pad' | 'number-pad' }) {
  return <View style={styles.field}><Text style={styles.fieldLabel}>{label}</Text><TextInput accessibilityLabel={label} value={value} onChangeText={onChangeText} placeholder={placeholder} placeholderTextColor={Colors.text3} keyboardType={keyboardType} style={styles.input} /></View>;
}

const styles = StyleSheet.create({
  darkHeader: { backgroundColor: Colors.surface, paddingHorizontal: 18, paddingTop: 15, paddingBottom: 14, gap: 12, borderBottomWidth: 1, borderBottomColor: Colors.border },
  headerTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 11 },
  headerTitle: { color: Colors.text1, fontSize: 20, fontWeight: '700', letterSpacing: -0.36 },
  monthRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 11 },
  monthButton: { borderRadius: 99 },
  monthButtonContent: { width: 25, height: 25, borderRadius: 13, alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.surfaceAlt, borderWidth: 1, borderColor: Colors.border },
  monthText: { color: Colors.text1, fontSize: 13, fontWeight: '700', minWidth: 110, textAlign: 'center' },
  sectionLabel: { color: Colors.text3, fontSize: 11, fontWeight: '700', letterSpacing: 1.08, marginBottom: 7 },
  surfaceCard: { backgroundColor: Colors.surface, borderRadius: 18, padding: 14, borderWidth: 1, borderColor: Colors.border },
  categoryPicker: { gap: 7, paddingVertical: 2 },
  chipPress: { borderRadius: 99 },
  chipCompact: { borderRadius: 99 },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 9, paddingVertical: 6, borderRadius: 99, backgroundColor: Colors.surface, borderWidth: 1, borderColor: Colors.border },
  chipSelected: { backgroundColor: Colors.primaryLt, borderColor: Colors.primary },
  chipEmoji: { fontSize: 13 },
  chipText: { fontSize: 12, fontWeight: '600', color: Colors.text2 },
  chipTextSelected: { color: Colors.primary },
  budgetRow: { gap: 5, marginBottom: 9 },
  budgetMeta: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 9 },
  budgetName: { flexDirection: 'row', alignItems: 'center', gap: 5, flex: 1 },
  budgetEmoji: { fontSize: 13 },
  budgetLabel: { color: Colors.text1, fontSize: 13, fontWeight: '600' },
  budgetAmount: { color: Colors.text2, fontSize: 12, fontWeight: '600', fontVariant: ['tabular-nums'] },
  track: { height: 7, borderRadius: 99, backgroundColor: Colors.bg, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 99 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(22,35,58,0.38)', justifyContent: 'flex-end' },
  modalSheet: { width: '100%', maxWidth: 430, maxHeight: Platform.OS === 'web' ? ('calc(100dvh - 12px)' as any) : '92%', alignSelf: 'center', backgroundColor: Colors.surface, borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingTop: 6, paddingBottom: 11 },
  handle: { width: 34, height: 4, borderRadius: 3, backgroundColor: '#D6D1C6', alignSelf: 'center', marginBottom: 4 },
  modalBody: { minHeight: 0, flexShrink: 1 },
  modalTop: { minHeight: 50, paddingHorizontal: 14, paddingVertical: 7, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 9, borderBottomWidth: 1, borderBottomColor: Colors.border },
  modalTitle: { flex: 1, color: Colors.text1, fontSize: 15, lineHeight: 20, fontWeight: '800', textAlign: 'center' },
  navPress: { borderRadius: 11, minWidth: 74 },
  cancelButton: { minHeight: 34, borderRadius: 11, paddingHorizontal: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.surfaceAlt },
  cancelText: { color: Colors.primary, fontSize: 13, fontWeight: '700' },
  formScroll: { flexShrink: 1, backgroundColor: Colors.bg },
  form: { gap: 9, paddingHorizontal: 16, paddingTop: 11, paddingBottom: 23 },
  field: { gap: 4 },
  fieldLabel: { color: Colors.text2, fontSize: 11, fontWeight: '700', letterSpacing: 0.54, textTransform: 'uppercase' },
  input: { height: 44, borderRadius: 11, borderWidth: 1, borderColor: Colors.border, backgroundColor: Colors.surface, paddingHorizontal: 13, color: Colors.text1, fontSize: 14 },
  saveButton: { minHeight: 34, borderRadius: 11, paddingHorizontal: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: Colors.primary },
  saveText: { color: '#FFFFFF', fontSize: 13, fontWeight: '700' },
});
